import React, { useState } from "react";
import { ClipboardList, Send, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

const CLINIC_WA_E164 = "+447445372190";

// Question groups — kept structured so the WhatsApp summary is readable.
const QUESTIONS = [
  { id: "q1",  text: "Frequent and heavy hair loss (for women)?", type: "yesno" },
  { id: "q2",  text: "Frequent pain between the shoulders, in the lower back, joints? Excessive nervousness/irritability?", type: "yesno" },
  { id: "q3",  text: "Digestive symptoms — choose ONE:", type: "letter",
    options: [
      { l: "A", t: "IBS irritation — chronic constipation / bloating / heartburn / belching" },
      { l: "B", t: "Feeling a moving lump in throat, stiffness in stomach, nausea on waking" },
      { l: "C", t: "Bloating and gas only" },
      { l: "D", t: "Kidney pain / urinary issues / chronic infections (women)" },
    ]},
  { id: "q4",  text: "Limb / spiritual symptoms — choose all that apply:", type: "letter_multi",
    options: [
      { l: "A", t: "Heaviness in feet, burning underneath, cramps, knee pain" },
      { l: "B", t: "Severe pain in feet, nerve tremors" },
      { l: "C", t: "Abnormal swellings in feet, extreme heat/cold in foot area" },
      { l: "D", t: "Suffocation/depression at home (after ʿAṣr, or upon seeing spouse)" },
      { l: "E1", t: "Constant arguments among family members" },
      { l: "E2", t: "Sudden appearance of ants/cockroaches that won't leave" },
      { l: "E3", t: "Valuable items disappearing" },
      { l: "E4", t: "Black mold in house corners" },
      { l: "E5", t: "Hearing a voice calling your name" },
      { l: "E6", t: "Light bulbs constantly burning out" },
      { l: "E7", t: "Smelling smoke/foul odours, shadows, black/white cloud, feeling someone behind you" },
    ]},
  { id: "q5",  text: "Frequent sexual dreams / nightmares?", type: "yesno" },
  { id: "q6",  text: "Masturbation addiction?", type: "yesno" },
  { id: "q7",  text: "Frequent miscarriages, or dreams of boys dying while girls survive (women)?", type: "yesno" },
  { id: "q8",  text: "Hormonal imbalance or irregular menstrual cycles (women)?", type: "yesno" },
  { id: "q9",  text: "Temporary infertility (both spouses medically healthy, tests fluctuate)?", type: "yesno" },
  { id: "q10", text: "PCOS in women?", type: "yesno" },
  { id: "q11", text: "Frequent marital problems, feelings of mutual repulsion?", type: "yesno" },
  { id: "q12", text: "Erectile dysfunction / sexual weakness?", type: "yesno" },
  { id: "q13", text: "Feeling suffocated during prayer or while reading the Quran?", type: "yesno" },
  { id: "q14", text: "Delay in marriage (no desire / no success / suffocation when meeting suitor)?", type: "yesno" },
  { id: "q15", text: "Marital status?", type: "select",
    options: [{ l: "single", t: "Single" }, { l: "married", t: "Married" },
              { l: "divorced", t: "Divorced" }, { l: "widowed", t: "Widowed" }]},
  { id: "q16", text: "Recurring dreams (graves, dead people, animals, exam failure, falling, flying, eating)? Specify in notes.", type: "yesno" },
  { id: "q17", text: "Spiritual heaviness — choose all that apply:", type: "letter_multi",
    options: [
      { l: "1", t: "Blasphemy/cursing during anger, waswās in faith, aversion to worship" },
      { l: "2", t: "Total lack of desire, lethargy, racing heartbeat" },
      { l: "3", t: "Severe forgetfulness, absentmindedness, sudden urge to cry" },
      { l: "4", t: "Excessive yawning during prayer/Qur'ān reading, pale/yellow face" },
      { l: "5", t: "Failure in any project you start" },
      { l: "6", t: "Heaviness in shoulders/above eyebrows/back of head, internal body heat" },
    ]},
  { id: "q18", text: "Sinus / nasal symptoms during Ruqyah?", type: "letter_multi",
    options: [
      { l: "1", t: "Runny/itchy nose during Qur'ān/Ruqyah, severe suffocation, chest tightness" },
      { l: "2", t: "Loss of sense of smell, chronic sinus inflammation" },
      { l: "3", t: "Intolerance to perfume smell, smelling foul odours, dental problems" },
    ]},
  { id: "q19", text: "Tasfeeh / Pharaonic Circumcision performed on you (women only)?", type: "yesno" },
  { id: "q20", text: "Tingling at base of head, thoughts of death, fear of night, feet heavy as if dragged?", type: "yesno" },
];


const DiagnosticQuestionnaire = () => {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const setAns = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
  const toggleMulti = (qid, letter) => {
    setAnswers((a) => {
      const cur = a[qid] || [];
      return { ...a, [qid]: cur.includes(letter) ? cur.filter(x => x !== letter) : [...cur, letter] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { alert("Please enter your name."); return; }
    if (!gender) { alert("Please select male or female."); return; }
    setSubmitting(true);
    try {
      // Persist on backend
      await api.post("/ruqyah/diagnostic-submit", {
        name, contact_phone: phone, contact_email: email,
        answers: { ...answers, gender, notes },
      });

      // Build WhatsApp message
      let lines = [
        "🌿 *Ruqyah Diagnostic Submission*",
        "Islamic Cure & Care — West London",
        "",
        `*Name:* ${name}`,
        `*Gender:* ${gender}`,
      ];
      if (phone) lines.push(`*Phone:* ${phone}`);
      if (email) lines.push(`*Email:* ${email}`);
      lines.push("");
      for (const q of QUESTIONS) {
        const a = answers[q.id];
        if (!a || (Array.isArray(a) && a.length === 0)) continue;
        lines.push(`*${q.text}*`);
        if (Array.isArray(a)) lines.push("  → " + a.join(", "));
        else lines.push("  → " + a);
        lines.push("");
      }
      if (notes.trim()) {
        lines.push("*Other notes / dreams / conditions:*");
        lines.push(notes);
      }
      const msg = encodeURIComponent(lines.join("\n"));
      const waUrl = `https://wa.me/${CLINIC_WA_E164.replace('+','')}?text=${msg}`;
      setDone({ waUrl });
      // automatically open WhatsApp
      window.open(waUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert("Could not submit. Please try again or WhatsApp us directly at " + CLINIC_WA_E164);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12 text-center">
        <CheckCircle2 className="w-16 h-16 text-forest mx-auto mb-4" />
        <h2 className="font-heading text-3xl text-forest mb-3">{t("diag_thanks")}</h2>
        <p className="text-mute mb-6">{t("diag_thanks_sub")}</p>
        <a
          data-testid="open-whatsapp-fallback"
          href={done.waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-gold text-ink font-semibold px-7 py-3 rounded-full"
        >
          {t("diag_open_wa")}
        </a>
      </div>
    );
  }

  const ynLabels = { "No": t("diag_ans_no"), "Sometimes": t("diag_ans_sometimes"), "Yes": t("diag_ans_yes") };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="diagnostic-questionnaire">
      <Link to="/ruqyah" className="inline-flex items-center gap-1 text-forest text-sm mb-6 hover:underline">
        <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("diag_back")}
      </Link>

      <div className="text-center mb-8">
        <ClipboardList className="w-10 h-10 text-gold mx-auto mb-3" />
        <h1 className="font-heading text-3xl sm:text-4xl text-forest font-semibold mb-2">
          {t("diag_title")}
        </h1>
        <p className="text-mute italic max-w-xl mx-auto">{t("diag_intro")}</p>
        <p className="text-xs text-clay italic mt-3">
          {t("diag_privacy_note")} ({CLINIC_WA_E164})
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6 bg-white rounded-3xl p-6 sm:p-8 border border-gold/25 shadow-sm">
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            data-testid="diag-name" required type="text" placeholder={t("diag_name_ph")}
            value={name} onChange={(e) => setName(e.target.value)}
            className="px-4 py-3 rounded-full border border-forest/20 focus:border-forest text-sm"
          />
          <input
            data-testid="diag-phone" type="tel" placeholder={t("diag_phone_ph")}
            value={phone} onChange={(e) => setPhone(e.target.value)}
            className="px-4 py-3 rounded-full border border-forest/20 focus:border-forest text-sm"
          />
          <input
            data-testid="diag-email" type="email" placeholder={t("diag_email_ph")}
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="px-4 py-3 rounded-full border border-forest/20 focus:border-forest text-sm"
          />
        </div>

        {/* Gender selector — required so the practitioner can tailor guidance */}
        <div data-testid="diag-gender-row" className="border-t border-gold/15 pt-5">
          <p className="font-medium text-forest mb-2">{t("diag_gender_label")}</p>
          <div className="flex gap-2">
            {[
              { key: "Male", label: t("diag_gender_male") },
              { key: "Female", label: t("diag_gender_female") },
            ].map((g) => (
              <button
                key={g.key}
                type="button"
                data-testid={`diag-gender-${g.key.toLowerCase()}`}
                onClick={() => setGender(g.key)}
                className={`px-6 py-2 rounded-full text-sm border transition ${
                  gender === g.key
                    ? "bg-forest text-sand border-forest"
                    : "bg-cream text-ink border-forest/20 hover:bg-cream/70"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {QUESTIONS.map((q, idx) => (
          <div key={q.id} data-testid={`diag-q-${q.id}`} className="border-t border-gold/15 pt-5">
            <p className="font-medium text-forest mb-2">{idx + 1}. {q.text}</p>

            {q.type === "yesno" && (
              <div className="flex gap-2">
                {["No", "Sometimes", "Yes"].map((v) => (
                  <button key={v} type="button" onClick={() => setAns(q.id, v)}
                    className={`px-4 py-1.5 rounded-full text-sm border transition ${
                      answers[q.id] === v
                        ? "bg-forest text-sand border-forest"
                        : "bg-cream text-ink border-forest/20 hover:bg-cream/70"
                    }`}>{ynLabels[v]}</button>
                ))}
              </div>
            )}

            {q.type === "letter" && (
              <div className="space-y-1.5">
                {q.options.map((o) => (
                  <button key={o.l} type="button" onClick={() => setAns(q.id, `${o.l}: ${o.t}`)}
                    className={`w-full text-left px-4 py-2 rounded-xl text-sm border transition ${
                      (answers[q.id] || "").startsWith(o.l + ":")
                        ? "bg-forest text-sand border-forest"
                        : "bg-cream text-ink border-forest/15 hover:bg-cream/70"
                    }`}>
                    <span className="font-bold mr-2">({o.l})</span>{o.t}
                  </button>
                ))}
              </div>
            )}

            {q.type === "letter_multi" && (
              <div className="space-y-1.5">
                {q.options.map((o) => {
                  const selected = (answers[q.id] || []).includes(`${o.l}: ${o.t}`);
                  return (
                    <button key={o.l} type="button" onClick={() => toggleMulti(q.id, `${o.l}: ${o.t}`)}
                      className={`w-full text-left px-4 py-2 rounded-xl text-sm border transition ${
                        selected
                          ? "bg-forest text-sand border-forest"
                          : "bg-cream text-ink border-forest/15 hover:bg-cream/70"
                      }`}>
                      <span className="font-bold mr-2">({o.l})</span>{o.t}
                    </button>
                  );
                })}
              </div>
            )}

            {q.type === "select" && (
              <div className="flex flex-wrap gap-2">
                {q.options.map((o) => (
                  <button key={o.l} type="button" onClick={() => setAns(q.id, o.t)}
                    className={`px-4 py-1.5 rounded-full text-sm border transition ${
                      answers[q.id] === o.t
                        ? "bg-forest text-sand border-forest"
                        : "bg-cream text-ink border-forest/20 hover:bg-cream/70"
                    }`}>{o.t}</button>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="border-t border-gold/15 pt-5">
          <p className="font-medium text-forest mb-2">{t("diag_notes_label")}</p>
          <textarea
            data-testid="diag-notes"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={4} placeholder={t("diag_notes_ph")}
            className="w-full px-4 py-3 rounded-2xl border border-forest/20 focus:border-forest text-sm"
          />
        </div>

        <button
          data-testid="diag-submit"
          type="submit" disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-6 py-4 rounded-full transition disabled:opacity-60"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          {submitting ? t("diag_sending") : t("diag_send")}
        </button>

        <p className="text-[11px] text-mute text-center italic">{t("diag_consent")}</p>
      </form>
    </div>
  );
};

export default DiagnosticQuestionnaire;
